import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { TemplateElement, TextAlign } from '../../models/receipt-template.model';
import { SAMPLE_DATA, CanvasDataMap, TableRow } from '../../models/merge-tag.model';

@Component({
  selector: 'app-canvas-element',
  templateUrl: './canvas-element.component.html',
  styleUrls: ['./canvas-element.component.scss'],
})
export class CanvasElementComponent implements OnChanges {

  @Input() element!: TemplateElement;
  @Input() isSelected = false;
  @Input() previewMode = false;
  /** When set, overrides SAMPLE_DATA for all display values (real job-card test-print). */
  @Input() customData: CanvasDataMap | null = null;
  @Input() scale = 3.7795;
  @Input() canvasWidthMm = 210;
  @Input() canvasHeightMm = 297;
  /** When true, drag and resize snap to the nearest 5 mm grid point. */
  @Input() snapToGrid = false;
  /** When true, this element's own drag is suppressed (canvas handles group drag instead). */
  @Input() disableDrag = false;
  /** When false, the 8 resize handles are hidden (used during multi-select). */
  @Input() showHandles = true;
  /**
   * The element's original Y position (mm) from the saved template — before any
   * push-down offset is applied by sortedElements.  Embedded as data-original-y
   * so the print window script can reset and recalculate positions in the print
   * rendering context (where image sizes may differ from the screen measurement).
   */
  @Input() originalYMm = 0;

  @Output() elementClicked  = new EventEmitter<MouseEvent>();
  @Output() moved           = new EventEmitter<TemplateElement>();
  /** Fires once when a drag or resize gesture fully completes (mouseup). Used to push history. */
  @Output() moveEnded       = new EventEmitter<TemplateElement>();
  @Output() removed         = new EventEmitter<TemplateElement>();
  /** Fires when the free-text content is committed (blur or Enter). */
  @Output() contentChanged  = new EventEmitter<TemplateElement>();
  /**
   * Fires when an attachment image finishes loading so the canvas can
   * re-run its overflow measurement and push elements below this one down.
   */
  @Output() contentResized  = new EventEmitter<void>();

  @ViewChild('textAreaEl') textAreaEl?: ElementRef<HTMLTextAreaElement>;

  public isEditing  = false;
  public isResizing = false;

  private resizeStart: {
    mx: number; my: number;
    w: number; h: number;
    ex: number; ey: number;
    handle: string;
  } | null = null;

  private dragStart: { mx: number; my: number; ex: number; ey: number } | null = null;

  ngOnChanges(_changes: SimpleChanges): void {}

  // ─── Computed display values ───────────────────────────────────────────────

  /** Active data map: real job-card data takes priority over SAMPLE_DATA. */
  private get dataMap(): CanvasDataMap | null {
    if (this.customData) return this.customData;
    if (this.previewMode) return SAMPLE_DATA;
    return null;
  }

  /** Returns the raw string value for the current tag, or null if it's a table/array. */
  private get scalarValue(): string | null {
    if (!this.dataMap) return null;
    const v = this.dataMap[this.element.tagKey];
    return Array.isArray(v) ? null : (v ?? '');
  }

  /** Scalar value shown for field/header elements. Returns the element label in design mode. */
  get displayValue(): string {
    if (!this.dataMap) return this.element.label || `{{${this.element.tagKey}}}`;
    return this.scalarValue ?? '';
  }

  /**
   * Returns the table rows when the data map holds a `TableRow[]` (array-of-arrays) for this tag.
   * Returns null in design mode or when the value is a `string[]` (image URL array).
   */
  get tableRows(): TableRow[] | null {
    if (!this.dataMap) return null;
    const v = this.dataMap[this.element.tagKey];
    if (!Array.isArray(v)) return null;
    if (v.length === 0) return [];
    // TableRow[] → first element is an array. string[] → first element is a string (URL).
    return Array.isArray(v[0]) ? (v as TableRow[]) : null;
  }

  /**
   * Returns the list of image URLs for this element.
   * Handles two shapes from CanvasDataMap:
   *   • `string`   — a single URL (logo, third_party_logo, …) → wrapped in a one-element array
   *   • `string[]` — multiple URLs (job-card attachment images)
   * Returns [] when no URL is present, which triggers the placeholder.
   */
  get imageUrls(): string[] {
    if (!this.dataMap) return [];
    const v = this.dataMap[this.element.tagKey];
    // Scalar URL string (logo, third_party_logo, etc.)
    if (typeof v === 'string') return v ? [v] : [];
    if (!Array.isArray(v) || v.length === 0) return [];
    // If the first element is itself an array it's a TableRow[] — not image URLs.
    return Array.isArray(v[0]) ? [] : (v as string[]);
  }

  /**
   * True when this element uses height:auto (grows beyond its configured height).
   * Exposed so the template can write it as data-is-auto for the print window script.
   */
  get isAutoHeightElement(): boolean {
    const imageDataIsArray = this.element.elementType === 'image' &&
      Array.isArray(this.dataMap?.[this.element.tagKey]);
    return (
      (this.element.elementType === 'table'         && this.tableRows !== null) ||
      (this.element.elementType === 'barcode-table' && this.tableRows !== null) ||
      (imageDataIsArray && this.imageUrls.length > 0) ||
       this.element.elementType === 'text' ||
       this.element.tagKey === 'customer_notes' ||
       this.element.tagKey === 'address'
    );
  }

  get isEmpty(): boolean {
    // These element types are always visible — they have no data-map key.
    if (this.element.elementType === 'text')    return false;
    if (this.element.elementType === 'divider') return false;
    if (!this.dataMap) return false;
    const v = this.dataMap[this.element.tagKey];
    // For table elements: empty when the array has no rows.
    if (Array.isArray(v)) return v.length === 0;
    return (v ?? '') === '';
  }

  get barcodeValue(): string {
    const val = this.scalarValue;
    return (val != null ? val : '') || 'JC00123';
  }

  get qrValue(): string {
    const val = this.scalarValue;
    return (val != null ? val : '') || 'JC-00123';
  }

  get barcodeHeight(): number {
    return Math.max(20, (this.element.height * this.scale) * 0.75);
  }

  /**
   * Height (px) of the barcode bars in each barcode-table row.
   * Uses a fixed ~10 mm at the current zoom level so that rows stay compact
   * regardless of how many line items are present.
   */
  get barcodeTableRowHeight(): number {
    return Math.max(20, this.scale * 10);
  }

  // ─── Column resize ────────────────────────────────────────────────────────

  /**
   * Number of columns inferred from live data (tableRows) when available,
   * falling back to sensible per-type defaults for design/placeholder mode.
   */
  get tableColumnCount(): number {
    if (this.tableRows && this.tableRows.length > 0) {
      // Skip group-header sentinel rows (['__group__', ...]) when determining
      // column count — data rows are the ones that drive column layout.
      const dataRow = this.tableRows.find(r => r[0] !== '__group__');
      return dataRow ? dataRow.length : this.tableRows[0].length;
    }
    // Placeholder defaults match the hardcoded placeholder row cell counts.
    return this.element.elementType === 'barcode-table' ? 4 : 2;
  }

  /**
   * Returns the effective column widths (%) for the current element.
   * Uses stored widths when they match the column count; otherwise computes
   * an equal distribution so all columns start at the same size.
   */
  get columnWidthsForTable(): number[] {
    const n = this.tableColumnCount;
    const stored = this.element.columnWidths;
    if (stored && stored.length === n) return stored;

    // ── Per-tag defaults ──────────────────────────────────────────────────
    // Add an entry here whenever you want a specific table tag to open with
    // non-equal column widths.  The array must have exactly `n` values that
    // sum to 100.  If the tag's column count changes (e.g. a future schema
    // update) the fallback below will kick in automatically.
    const defaults: Record<string, number[]> = {
      line_items:        [60, 10, 30],      // description | qty | total
      line_item_barcode: [30, 30, 10, 30],  // barcode | description | qty | total
      totals:            [70, 30],          // label | amount
      external_notes:    [10, 90],          // date | notes
      internal_notes:    [10, 90],          // date | notes
    };
    const tagDefault = defaults[this.element.tagKey];
    if (tagDefault && tagDefault.length === n) return tagDefault;

    // Equal distribution — last column absorbs any floating-point remainder.
    const w = parseFloat((100 / n).toFixed(4));
    return [
      ...Array(n - 1).fill(w),
      parseFloat((100 - w * (n - 1)).toFixed(4)),
    ];
  }

  /**
   * Starts a column-resize drag.  Moving the divider at colIndex redistributes
   * width between column colIndex and column colIndex+1, keeping all others
   * unchanged.  Emits via the standard moved/moveEnded outputs so the editor
   * history and persistence work for free.
   */
  /**
   * Returns the effective per-column text alignments.
   * Uses stored alignments when present; falls back to per-tag defaults;
   * then falls back to all-left.  Add entries to `defaults` here to set
   * sensible alignments for any tag without the user having to configure them.
   */
  get columnAlignmentsForTable(): TextAlign[] {
    const n = this.tableColumnCount;
    const stored = this.element.columnAlignments;
    if (stored && stored.length === n) return stored;

    // ── Per-tag defaults ──────────────────────────────────────────────────
    const defaults: Record<string, TextAlign[]> = {
      line_items:        ['left',   'left', 'right'],
      line_item_barcode: ['center', 'left', 'left', 'right'],
      totals:            ['left',   'right'],
    };
    const tagDefault = defaults[this.element.tagKey];
    if (tagDefault && tagDefault.length === n) return tagDefault;

    return Array(n).fill('left') as TextAlign[];
  }

  // ─── Custom-questions table helpers ───────────────────────────────────────

  /**
   * Returns true when a table row is a group-header sentinel row.
   * Sentinel format: ['__group__', 'Group Name']
   */
  isGroupHeaderRow(row: TableRow): boolean {
    return row.length >= 1 && row[0] === '__group__';
  }

  /**
   * Returns true when this table is a notes table (external_notes or internal_notes).
   * Notes tables have two columns: [date, note] and get special CSS styling.
   */
  isNotesTable(): boolean {
    return this.element.tagKey === 'external_notes' || this.element.tagKey === 'internal_notes';
  }

  /**
   * Returns true when a cell value is an encoded checkbox marker.
   * Markers: '__cb:1__' (checked) | '__cb:0__' (unchecked)
   */
  isCheckboxCell(cell: string): boolean {
    return cell === '__cb:1__' || cell === '__cb:0__';
  }

  /** Returns true when the checkbox marker encodes a checked state. */
  checkboxChecked(cell: string): boolean {
    return cell === '__cb:1__';
  }

  /** Returns true when a cell value is an encoded traffic-light marker (__tl:…__). */
  isTrafficLightCell(cell: string): boolean {
    return typeof cell === 'string' && cell.startsWith('__tl:') && cell.endsWith('__');
  }

  /** Extracts the traffic-light selection value (e.g. 'Yes', 'No', 'Not applicable'). */
  trafficLightValue(cell: string): string {
    if (!this.isTrafficLightCell(cell)) return '';
    const inner = cell.slice(5, -2); // strip '__tl:' and '__'
    const pipe = inner.indexOf('|');
    return pipe === -1 ? inner : inner.slice(0, pipe);
  }

  /** Extracts the unlabelled text component (|T:) from a non-front/rear traffic-light cell. */
  trafficLightText(cell: string): string {
    if (!this.isTrafficLightCell(cell)) return '';
    const inner = cell.slice(5, -2);
    const m = inner.match(/\|T:([^|]*)/);
    return m ? m[1] : '';
  }

  /** Extracts the front (F:) label from a traffic-light cell, or '' if absent. */
  trafficLightFront(cell: string): string {
    if (!this.isTrafficLightCell(cell)) return '';
    const m = cell.match(/\|F:([^|]*)/);
    return m ? m[1] : '';
  }

  /** Extracts the rear (R:) label from a traffic-light cell, or '' if absent. */
  trafficLightRear(cell: string): string {
    if (!this.isTrafficLightCell(cell)) return '';
    const inner = cell.slice(5, -2); // strip '__tl:' and '__' so the trailing __ isn't captured
    const m = inner.match(/\|R:([^|]*)/);
    return m ? m[1] : '';
  }

  /** Returns the PrimeNG icon class for a given alignment value. */
  alignIcon(align: TextAlign): string {
    return align === 'right'  ? 'pi pi-align-right'
         : align === 'center' ? 'pi pi-align-center'
         : 'pi pi-align-left';
  }

  /**
   * Cycles a single column's alignment left → center → right → left.
   * Emits via moveEnded so the change is persisted and pushed to history.
   */
  cycleColumnAlignment(colIndex: number): void {
    if (this.previewMode) return;
    const alignments = [...this.columnAlignmentsForTable];
    const order: TextAlign[] = ['left', 'center', 'right'];
    const next = order[(order.indexOf(alignments[colIndex]) + 1) % order.length];
    alignments[colIndex] = next;
    this.moveEnded.emit({ ...this.element, columnAlignments: alignments });
  }

  startColumnResize(event: MouseEvent, colIndex: number): void {
    if (this.previewMode) return;
    event.stopPropagation();
    event.preventDefault();

    const startWidths = [...this.columnWidthsForTable];
    if (colIndex >= startWidths.length - 1) return;

    const startX       = event.clientX;
    const elWidthPx    = this.element.width * this.scale;
    const MIN_PCT      = 5; // minimum column width in percent

    const compute = (clientX: number): number[] => {
      const dxPct   = ((clientX - startX) / elWidthPx) * 100;
      const maxDelta =  startWidths[colIndex + 1] - MIN_PCT;
      const minDelta =  MIN_PCT - startWidths[colIndex];
      const delta    = Math.max(minDelta, Math.min(maxDelta, dxPct));
      const w = [...startWidths];
      w[colIndex]     = parseFloat((startWidths[colIndex]     + delta).toFixed(2));
      w[colIndex + 1] = parseFloat((startWidths[colIndex + 1] - delta).toFixed(2));
      return w;
    };

    const onMove = (mv: MouseEvent) => {
      this.moved.emit({ ...this.element, columnWidths: compute(mv.clientX) });
    };

    const onUp = (upEvent: MouseEvent) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      this.moveEnded.emit({ ...this.element, columnWidths: compute(upEvent.clientX) });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  get qrSize(): number {
    return Math.max(40, Math.min(this.element.width, this.element.height) * this.scale - 16);
  }

  // ─── Styles ───────────────────────────────────────────────────────────────
  get elementStyle(): Record<string, string> {
    // Table elements (live data) and free-text elements both use height:auto so
    // they grow to show all content.  minHeight keeps the configured size as a
    // floor so the element stays grabbable on the canvas even when empty.
    // Image elements only auto-grow when their data is an array (attachment grid).
    // Scalar-URL images (logo, third_party_logo) stay within their configured bounds.
    const imageDataIsArray = this.element.elementType === 'image' &&
      Array.isArray(this.dataMap?.[this.element.tagKey]);
    const isAutoHeight =
      (this.element.elementType === 'table'         && this.tableRows !== null) ||
      (this.element.elementType === 'barcode-table' && this.tableRows !== null) ||
      (imageDataIsArray && this.imageUrls.length > 0) ||
       this.element.elementType === 'text' ||
       this.element.tagKey === 'customer_notes' ||
       this.element.tagKey === 'address';
    const configuredHeightPx = `${this.element.height * this.scale}px`;
    return {
      left:            `${this.element.x * this.scale}px`,
      top:             `${this.element.y * this.scale}px`,
      width:           `${this.element.width * this.scale}px`,
      height:          isAutoHeight ? 'auto' : configuredHeightPx,
      minHeight:       isAutoHeight ? configuredHeightPx : '',
      zIndex:          `${this.element.zIndex}`,
      color:           this.element.color,
      backgroundColor: this.element.backgroundColor === 'transparent' ? 'transparent' : this.element.backgroundColor,
    };
  }

  get textStyle(): Record<string, string> {
    return {
      fontSize:   `${this.element.fontSize * (this.scale / 3.7795)}px`,
      fontWeight: this.element.fontWeight,
      fontStyle:  this.element.fontStyle,
      textAlign:  this.element.textAlign,
    };
  }

  get labelStyle(): Record<string, string> {
    return {
      fontSize:   `${Math.max(7, this.element.fontSize * 0.75 * (this.scale / 3.7795))}px`,
      fontWeight: 'bold',
      color:      this.element.color,
    };
  }

  // ─── Inline text editing ──────────────────────────────────────────────────

  startEdit(event: MouseEvent): void {
    if (this.previewMode) return;
    if (this.element.elementType !== 'text') return;
    event.stopPropagation();
    this.isEditing = true;
    // Focus and auto-size after *ngIf renders the textarea
    setTimeout(() => {
      const ta = this.textAreaEl?.nativeElement;
      if (ta) {
        ta.focus();
        this.fitTextarea(ta);
      }
    }, 0);
  }

  /** Resize the textarea to exactly its scroll-height so no content is clipped. */
  private fitTextarea(ta: HTMLTextAreaElement): void {
    ta.style.height = 'auto';
    ta.style.height = `${ta.scrollHeight}px`;
  }

  /** Called on every keystroke inside the textarea to keep it fitted. */
  onTextareaInput(): void {
    const ta = this.textAreaEl?.nativeElement;
    if (ta) this.fitTextarea(ta);
  }

  stopEdit(): void {
    // Guard: if isEditing was already cleared (e.g. by Escape), do nothing
    if (!this.isEditing) return;
    this.isEditing = false;
    const value = this.textAreaEl?.nativeElement?.value ?? (this.element.content ?? '');
    this.contentChanged.emit({ ...this.element, content: value });
  }

  onEditKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      // Set flag to false BEFORE blur fires so stopEdit() bails out
      this.isEditing = false;
    }
  }

  // ─── Grid snap helper ─────────────────────────────────────────────────────
  private static readonly GRID_MM = 5;

  /** Rounds a mm value to the nearest grid point (no-op when snap is off). */
  private snap(valueMm: number): number {
    if (!this.snapToGrid) return valueMm;
    return Math.round(valueMm / CanvasElementComponent.GRID_MM) * CanvasElementComponent.GRID_MM;
  }

  // ─── Drag (reposition on canvas) ─────────────────────────────────────────
  startDrag(event: MouseEvent): void {
    // Ignore if a resize handle was the target (resize handles call stopPropagation,
    // but guard here too for safety), if already resizing, or if in text-edit mode.
    if (this.previewMode) return;
    if (this.isEditing) return;
    if (this.isResizing) return;
    if (this.disableDrag) return;   // canvas handles group drag
    if ((event.target as HTMLElement).classList.contains('ce-handle')) return;

    event.preventDefault();
    // NOTE: do NOT stopPropagation here — the (click) handler needs the click
    // to bubble so that onClick() can fire for element selection.

    const startX = this.element.x;
    const startY = this.element.y;

    this.dragStart = { mx: event.clientX, my: event.clientY, ex: startX, ey: startY };

    const onMove = (mv: MouseEvent) => {
      if (!this.dragStart) return;
      const dx = (mv.clientX - this.dragStart.mx) / this.scale;
      const dy = (mv.clientY - this.dragStart.my) / this.scale;
      const newX = Math.max(0, Math.min(this.snap(this.dragStart.ex + dx), this.canvasWidthMm  - this.element.width));
      const newY = Math.max(0, Math.min(this.snap(this.dragStart.ey + dy), this.canvasHeightMm - this.element.height));
      this.moved.emit({ ...this.element, x: +newX.toFixed(2), y: +newY.toFixed(2) });
    };

    const onUp = (upEvent: MouseEvent) => {
      if (!this.dragStart) return;
      const dx = (upEvent.clientX - this.dragStart.mx) / this.scale;
      const dy = (upEvent.clientY - this.dragStart.my) / this.scale;
      const newX = Math.max(0, Math.min(this.snap(this.dragStart.ex + dx), this.canvasWidthMm  - this.element.width));
      const newY = Math.max(0, Math.min(this.snap(this.dragStart.ey + dy), this.canvasHeightMm - this.element.height));
      const hasMoved = Math.abs(newX - this.dragStart.ex) > 0.05 || Math.abs(newY - this.dragStart.ey) > 0.05;
      this.dragStart = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      if (hasMoved) {
        this.moveEnded.emit({ ...this.element, x: +newX.toFixed(2), y: +newY.toFixed(2) });
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  // ─── Resize ───────────────────────────────────────────────────────────────
  startResize(event: MouseEvent, handle: string): void {
    if (this.previewMode) return;
    event.stopPropagation();
    event.preventDefault();
    this.isResizing = true;
    this.resizeStart = {
      mx: event.clientX, my: event.clientY,
      w:  this.element.width,  h:  this.element.height,
      ex: this.element.x,      ey: this.element.y,
      handle,
    };

    const calc = (mv: MouseEvent) => {
      if (!this.resizeStart) return null;
      const dx = (mv.clientX - this.resizeStart.mx) / this.scale;
      const dy = (mv.clientY - this.resizeStart.my) / this.scale;
      let w  = this.resizeStart.w;
      let h  = this.resizeStart.h;
      let ex = this.resizeStart.ex;
      let ey = this.resizeStart.ey;

      // For each handle, snap the moving edge to the nearest grid point.
      // The opposite edge is anchored so snapping one side never moves the other.
      if (handle.includes('e')) {
        const right = this.snap(this.resizeStart.ex + this.resizeStart.w + dx);
        w = Math.max(5, right - ex);
      }
      if (handle.includes('s')) {
        const bottom = this.snap(this.resizeStart.ey + this.resizeStart.h + dy);
        h = Math.max(3, bottom - ey);
      }
      if (handle.includes('w')) {
        const left = this.snap(this.resizeStart.ex + dx);
        const fixedRight = this.resizeStart.ex + this.resizeStart.w;
        const nw = Math.max(5, fixedRight - left);
        ex = fixedRight - nw;   // anchor the right edge
        w  = nw;
      }
      if (handle.includes('n')) {
        const top = this.snap(this.resizeStart.ey + dy);
        const fixedBottom = this.resizeStart.ey + this.resizeStart.h;
        const nh = Math.max(3, fixedBottom - top);
        ey = fixedBottom - nh;  // anchor the bottom edge
        h  = nh;
      }
      return { x: +ex.toFixed(2), y: +ey.toFixed(2), width: +w.toFixed(2), height: +h.toFixed(2) };
    };

    const onMove = (mv: MouseEvent) => {
      const dims = calc(mv);
      if (dims) this.moved.emit({ ...this.element, ...dims });
    };

    const onUp = (upEvent: MouseEvent) => {
      this.isResizing = false;
      const dims = calc(upEvent);
      this.resizeStart = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
      if (dims) this.moveEnded.emit({ ...this.element, ...dims });
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }

  onClick(event: MouseEvent): void {
    if (this.previewMode) { event.stopPropagation(); return; }
    event.stopPropagation();
    this.elementClicked.emit(event);
  }
}
